import { In, MoreThan } from 'typeorm'
import { Arg, Args, Ctx, FieldResolver, Query, Resolver, Root, Directive } from 'type-graphql'

import { User } from '@things-factory/auth-base'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'
import { checkTarget, checkDomain, getPermission } from '@things-factory/operato-license-checker'

import { Group } from '../group/group.js'
import { PlayGroup } from '../play-group/play-group.js'
import { Board } from './board.js'
import { BoardList } from './board-type.js'
import { BoardHistory } from './board-history.js'

import { LicenseError } from '../../errors/license-error.js'
@Resolver(Board)
export class BoardQuery {
  @Directive('@privilege(category: "board", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => Board, { description: 'Finds a single board by its ID. If cachedUpdatedAt matches, model is omitted.' })
  async board(
    @Arg('id') id: string,
    @Arg('cachedUpdatedAt', { nullable: true, description: 'Client cache timestamp — if matches, model field is omitted' }) cachedUpdatedAt: string,
    @Ctx() context: ResolverContext
  ): Promise<Board> {
    const { domain } = context.state

    var board = await getRepository(Board).findOne({
      where: { domain: { id: In([domain.id, domain.parentId].filter(Boolean)) }, id }
    })

    // 캐시 유효성 체크: updatedAt이 동일하면 model을 생략하여 전송량 절감
    if (board && cachedUpdatedAt && board.updatedAt?.toISOString() === cachedUpdatedAt) {
      board.model = null
    }

    if (domain) {
      // 1. check domainSecret is over limit quantity or not
      var count = await getRepository(Domain)
        .manager.createQueryBuilder()
        .select('id')
        .from(Domain, 'domain')
        .getCount()

      if (!checkDomain(count)) {
        throw new LicenseError({
          errorCode: context.t(LicenseError.ERROR_CODES.OVER_LIMIT)
        })
      }
    }

    if (board) {
      // 1. check boardSecret is over limit quantity or not
      var count = await getRepository(Board)
        .manager.createQueryBuilder()
        .select('id')
        .from(Board, 'board')
        .where('"domain_id" = :domainId', { domainId: domain.id })
        .getCount()

      if (!checkTarget(count)) {
        throw new LicenseError({
          errorCode: context.t(LicenseError.ERROR_CODES.OVER_LIMIT)
        })
      }
    }

    return board
  }

  @Query(returns => Board, { nullable: true, description: 'Finds a single board by its name.' })
  @Directive('@privilege(category: "board", privilege: "query", domainOwnerGranted: true)')
  async boardByName(@Arg('name') name: string, @Ctx() context: ResolverContext): Promise<Board> {
    const { domain } = context.state

    return await getRepository(Board).findOne({
      where: { domain: { id: In([domain.id, domain.parentId].filter(Boolean)) }, name }
    })
  }

  @Query(returns => BoardList, { description: 'Retrieves a paginated list of boards created by the current user.' })
  async boardsCreatedByMe(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<BoardList> {
    const { domain, user } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(Board),
      params,
      domain,
      alias: 'board',
      searchables: ['name', 'description']
    }).andWhere('board.creator = :user', { user: user.id })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @Query(returns => [BoardHistory], { description: 'Retrieves up to 10 historical versions of a specific board.' })
  @Directive('@privilege(category: "board", privilege: "query", domainOwnerGranted: true)')
  async boardVersions(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<BoardHistory[]> {
    const { domain } = context.state

    return await getRepository(BoardHistory).find({
      where: { domain: { id: In([domain.id, domain.parentId].filter(Boolean)) }, originalId: id },
      relations: ['updater'],
      order: { version: 'DESC' },
      take: 10
    })
  }

  @Query(returns => BoardHistory, { description: 'Retrieves the most recently published version of a board.' })
  @Directive('@privilege(category: "board", privilege: "query", domainOwnerGranted: true)')
  async boardPublished(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<BoardHistory> {
    const { domain } = context.state

    return await getRepository(BoardHistory).findOne({
      where: { domain: { id: In([domain.id, domain.parentId].filter(Boolean)) }, originalId: id },
      order: { version: 'DESC' }
    })
  }

  @Query(returns => BoardList, { description: 'Retrieves a paginated list of all boards.' })
  @Directive('@privilege(category: "board", privilege: "query", domainOwnerGranted: true)')
  async boards(@Args(type => ListParam) params: ListParam, @Ctx() context: ResolverContext): Promise<BoardList> {
    const { domain } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(Board),
      params,
      domain,
      searchables: ['name', 'description']
    })

    // 클라이언트 정렬 지정이 없으면 sortOrder 기본 정렬
    if (!params.sortings?.length) {
      queryBuilder.addOrderBy(`"${queryBuilder.alias}"."sort_order"`, 'ASC', 'NULLS LAST')
      queryBuilder.addOrderBy(`"${queryBuilder.alias}"."created_at"`, 'ASC')
    }

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @Query(returns => [Board], {
    description: 'Retrieves boards that have been updated or soft-deleted since the given timestamp.'
  })
  @Directive('@privilege(category: "board", privilege: "query", domainOwnerGranted: true)')
  async boardsUpdatedSince(
    @Arg('since') since: Date,
    @Ctx() context: ResolverContext
  ): Promise<Board[]> {
    const { domain } = context.state

    return await getRepository(Board)
      .createQueryBuilder('board')
      .withDeleted()
      .where('board.domain_id = :domainId', { domainId: domain.id })
      .andWhere('board.updated_at > :since', { since })
      .getMany()
  }

  @FieldResolver(type => Group, { description: 'Resolves the group that the board belongs to.' })
  async group(@Root() board: Board) {
    return (
      board.groupId &&
      (await getRepository(Group).findOneBy({
        id: board.groupId
      }))
    )
  }

  @FieldResolver(type => [PlayGroup], {
    description: 'Resolves the list of play groups that this board is a member of.'
  })
  async playGroups(@Root() board: Board) {
    return (
      await getRepository(Board).findOne({
        where: { id: board.id },
        relations: ['playGroups']
      })
    )?.playGroups
  }

  @FieldResolver(type => Domain, { description: 'Resolves the domain associated with the board.' })
  async domain(@Root() board: Board) {
    return (
      board.domainId &&
      (await getRepository(Domain).findOneBy({
        id: board.domainId
      }))
    )
  }

  @FieldResolver(type => User, { description: 'Resolves the user who last updated the board.' })
  async updater(@Root() board: Board): Promise<User> {
    return (
      board.updaterId &&
      (await getRepository(User).findOneBy({
        id: board.updaterId
      }))
    )
  }

  @FieldResolver(type => User, { description: 'Resolves the user who created the board.' })
  async creator(@Root() board: Board): Promise<User> {
    return (
      board.creatorId &&
      (await getRepository(User).findOneBy({
        id: board.creatorId
      }))
    )
  }
}
